import { Boxes, CheckCircle2, Crown, Layers, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { getPositionStats } from '@/api/member';

const fmt = (n) => {
  const v = Number(n || 0);
  if (!Number.isFinite(v)) return '0';
  return v.toLocaleString('en-US');
};

const Tile = ({ icon: Icon, label, value, testid }) => (
  <div
    className="rounded-xl p-3 md:p-4 border relative overflow-hidden"
    style={{
      background: 'rgba(0,0,0,0.32)',
      borderColor: 'rgba(255, 182, 18, 0.18)',
    }}
    data-testid={testid}
  >
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.20em] text-[var(--sc-text-mute)]">
      <Icon className="w-3.5 h-3.5 text-[#FFC20E]" />
      {label}
    </div>
    <div className="sc-tabular font-display font-bold text-2xl md:text-3xl mt-1.5 leading-none sc-gold-text" data-testid={`${testid}-value`}>
      {value}
    </div>
  </div>
);

const PositionStatsStrip = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let alive = true;
    const probe = async () => {
      try {
        const r = await getPositionStats();
        if (!alive) return;
        setData(r);
        setErrored(false);
      } catch {
        if (!alive) return;
        setErrored(true);
      } finally {
        if (alive) setLoading(false);
      }
    };
    probe();
    // Poll every 60s (slow-poll, 2025-06): position counters only change
    // when a cycle completes, which is once per minute at most.
    const t = setInterval(probe, 60000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  if (loading && !data) {
    return (
      <section className="sc-glass rounded-2xl p-5 md:p-6 relative overflow-hidden" data-testid="position-stats-strip-loading">
        <div className="flex items-center gap-3 text-[var(--sc-text-mute)] text-[12px] uppercase tracking-[0.22em]">
          <Loader2 className="w-4 h-4 animate-spin text-[#FFC20E]" />
          Loading position activity…
        </div>
      </section>
    );
  }

  if (errored || !data) {
    return (
      <section className="sc-glass rounded-2xl p-5 md:p-6 relative overflow-hidden" data-testid="position-stats-strip-error">
        <div className="text-[12px] text-[var(--sc-text-mute)]">Position activity temporarily unavailable. Reconnecting…</div>
      </section>
    );
  }

  return (
    <section className="sc-glass rounded-2xl p-5 md:p-6 relative overflow-hidden" data-testid="position-stats-strip">
      <div
        className="absolute -top-16 -right-16 w-44 h-44 pointer-events-none rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(255,182,18,0.12), transparent 60%)' }}
        aria-hidden="true"
      />
      <div className="relative z-10 flex items-center justify-between mb-4">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">Your Position Activity</div>
        <span
          className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 rounded-full border"
          style={{
            color: '#FFE08A',
            background: 'rgba(255,182,18,0.10)',
            borderColor: 'rgba(255,182,18,0.32)',
          }}
        >
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#FFC20E] animate-pulse" aria-hidden="true" />
          Live
        </span>
      </div>
      <div className="relative z-10 grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <Tile icon={Boxes} label="Total Positions" value={fmt(data.total_positions)} testid="position-stat-total" />
        <Tile icon={Crown} label="Active Roots" value={fmt(data.active_roots)} testid="position-stat-roots" />
        <Tile icon={Layers} label="Active Fillers" value={fmt(data.active_fillers)} testid="position-stat-fillers" />
        <Tile icon={CheckCircle2} label="Total Positions Cycled" value={fmt(data.total_positions_cycled)} testid="position-stat-cycled" />
      </div>
    </section>
  );
};

export default PositionStatsStrip;
